"use client"

import Image, { type ImageProps } from "next/image"
import { useState, useCallback, useEffect } from "react"
import { isFridgeFallbackImage } from "@/lib/fridge-fallback-image"
import { getProxiedStoragePath } from "@/lib/proxied-storage-path"

const PLACEHOLDER_SRC = "/placeholder.svg"

interface SafeImageProps extends ImageProps {
  fallbackSrc?: string
  /** Optional family-specific visual treatment for an absent or failed photo. */
  fallbackContent?: React.ReactNode
  /**
   * FRIDGE-635 (owner ruling 08/29/2026): on PRODUCT surfaces, a missing or
   * dead photo falls back to the product's BRAND logo first — "if no photo
   * use logo of brand" — and only then to the Fridge.com mark. Pass the
   * brand's logo URL (brands.logo_path, the same asset brand pages render);
   * when it is absent or itself fails, the chain continues to fallbackSrc.
   */
  brandLogoSrc?: string | null
}

/**
 * The FRIDGE-635 fallback chain, extracted pure for unit testing: which URL
 * renders after N load errors. 0 errors → the real src; 1 → the brand logo
 * (when provided); then the house mark. With no brand logo the mark comes
 * one step earlier.
 */
export function resolveFallbackChain(
  errorCount: number,
  src: string,
  brandLogoSrc: string | null | undefined,
  fallbackSrc: string
): string {
  const chain = [src, ...(brandLogoSrc ? [brandLogoSrc] : []), fallbackSrc]
  return chain[Math.min(errorCount, chain.length - 1)]
}

function normalizeImageSrc(src: ImageProps["src"]): ImageProps["src"] {
  if (typeof src !== "string") {
    return src
  }

  // Prevent mixed content by upgrading external HTTP image URLs to HTTPS.
  const httpsUpgraded = src.startsWith("http://")
    ? `https://${src.slice("http://".length)}`
    : src

  // CDN lane (owner directive 07/20): a proxied Supabase Storage URL — most
  // importantly a product-images photo — is rewritten to its same-origin
  // `/<bucket>/<path>`, so no raw `*.supabase.co` host reaches the browser in
  // the rendered <img> src, the srcset, OR the `/_next/image?url=` param. This
  // is the single display chokepoint: every product card, gallery, search
  // result, comparison table, and recommendation rail renders through
  // SafeImage/BrandedImage, so they are all covered here. Non-proxied inputs
  // (external CDNs, /placeholder.svg) pass through untouched.
  return getProxiedStoragePath(httpsUpgraded)
}

/**
 * Which URL belongs in the rendered `src` attribute — the SEO override, or
 * nothing (letting the fallback through).
 *
 * Keeping the canonical URL in `src` is what makes Google Image Search index
 * the real image rather than the `/_next/image` proxy: the external CDN URL
 * for third-party images, or the same-origin `/product-images/...` path for
 * our CDN-lane photos (no `*.supabase.co` host leaks to the browser).
 *
 * THE `hasErrored` BRANCH IS THE WHOLE POINT, and it was missing.
 * `overrideSrc` sets the rendered `src` outright, so applying it
 * unconditionally made this component's fallback UNREACHABLE for every
 * external image: `onError` swapped internal state to the placeholder, the
 * next render re-pinned `src` to the dead URL, and the browser showed raw alt
 * text. SafeImage was not safe in the exact case it exists for.
 *
 * Found 08/06/2026 on `/collections/french-door-refrigerators`, whose first
 * card points at a BrandsMart URL that now answers 404 at the source. 19
 * active products carry a first image on that host — but the defect applies
 * to ANY external host that goes dead, which is why the fix belongs here and
 * not in those rows.
 *
 * Dropping the override on failure costs nothing for SEO: the canonical URL
 * is dead, so there is nothing left to index.
 *
 * Extracted as a pure function so it is unit-testable without a DOM — this
 * repository's jest setup matches `*.test.ts` and carries no React renderer.
 */
export function resolveSeoOverride(
  normalizedSrc: ImageProps["src"],
  explicitOverride: string | undefined,
  hasErrored: boolean
): string | undefined {
  if (hasErrored) return undefined
  if (explicitOverride) return explicitOverride
  return typeof normalizedSrc === "string" &&
    (normalizedSrc.startsWith("http") || normalizedSrc.startsWith("/product-images/"))
    ? normalizedSrc
    : undefined
}

/**
 * Wrapper around next/image that:
 * 1. Falls back to a placeholder when an image fails to load (404, CDN down, etc.)
 * 2. Preserves the original URL in the rendered `src` attribute via `overrideSrc`
 *    so Google Image Search indexes the canonical URL, not the /_next/image proxy.
 */
export function SafeImage({
  src,
  alt,
  fallbackSrc = PLACEHOLDER_SRC,
  brandLogoSrc,
  fallbackContent,
  onError: externalOnError,
  overrideSrc,
  ...props
}: SafeImageProps) {
  const normalizedSrc = normalizeImageSrc(src)
  const [errorCount, setErrorCount] = useState(0)

  // Reset when the `src` prop changes, ADJUSTED DURING RENDER rather than from
  // an effect. This is React's documented answer for derived-from-prop state,
  // and the shape react-hooks/set-state-in-effect asks for: the effect version
  // painted one frame with the previous image before correcting itself, so
  // changing src briefly showed the old picture. React re-runs this component
  // immediately without committing the intermediate render.
  const [prevSrc, setPrevSrc] = useState(normalizedSrc)
  if (prevSrc !== normalizedSrc) {
    setPrevSrc(normalizedSrc)
    setErrorCount(0)
  }

  const hasErrored = errorCount > 0

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // Each failed stage advances the FRIDGE-635 chain: real photo → brand
      // logo (when provided) → the house mark. Bounded, so a dead final
      // fallback cannot loop.
      setErrorCount((n) => Math.min(n + 1, 2))
      externalOnError?.(e)
    },
    [externalOnError]
  )

  if (fallbackContent && (!normalizedSrc || hasErrored || (typeof normalizedSrc === 'string' && (isFridgeFallbackImage(normalizedSrc) || /\/placeholder\.svg(?:[?#]|$)/i.test(normalizedSrc))))) return <>{fallbackContent}</>

  if (!normalizedSrc || normalizedSrc === "") {
    // No source at all: start the chain at the brand logo when we have one
    // (FRIDGE-635 — the owner's "if no photo use logo of brand"), falling to
    // the house mark if the logo itself dies.
    return (
      <Image
        {...props}
        src={brandLogoSrc && errorCount === 0 ? brandLogoSrc : fallbackSrc}
        alt={alt}
        onError={handleError}
      />
    )
  }

  const seoOverride = resolveSeoOverride(normalizedSrc, overrideSrc, hasErrored)
  const imgSrc =
    typeof normalizedSrc === "string"
      ? resolveFallbackChain(errorCount, normalizedSrc, brandLogoSrc, fallbackSrc)
      : normalizedSrc

  return (
    <Image
      {...props}
      src={imgSrc}
      alt={alt}
      onError={handleError}
      overrideSrc={seoOverride}
    />
  )
}
